import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import mermaid from 'mermaid';
import './ReportDocumentation.css';
import manifest from '../documentation_manifest.json';
import { documentationContent } from '../documentationContent';

// Recursive Sidebar Item Component
const SidebarItem = ({ name, node, onSelectFile, selectedFile, depth = 0 }) => {
    const [isOpen, setIsOpen] = useState(depth < 1); // Open top-level by default
    const hasFiles = node._files && node._files.length > 0;

    if (name === '_files') return null; // Handled within parent

    const handleToggle = (e) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    return (
        <li className="sidebar-item">
            <div className="item-header" onClick={handleToggle} style={{ paddingLeft: `${depth * 15 + 10}px` }}>
                <span className="icon">{isOpen ? '📂' : '📁'}</span>
                <span className="label">{name}</span>
            </div>

            {isOpen && (
                <ul className="sub-list">
                    {/* Render Subfolders */}
                    {Object.keys(node).map(key => {
                        if (key === '_files') return null;
                        return (
                            <SidebarItem
                                key={key}
                                name={key}
                                node={node[key]}
                                onSelectFile={onSelectFile}
                                selectedFile={selectedFile}
                                depth={depth + 1}
                            />
                        );
                    })}

                    {/* Render Files */}
                    {hasFiles && node._files.map(file => (
                        <li
                            key={file.path}
                            className={`file-item ${selectedFile?.path === file.path ? 'active' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onSelectFile(file);
                            }}
                            style={{ paddingLeft: `${(depth + 1) * 15 + 10}px` }}
                        >
                            <span className="file-icon">📄</span>
                            {file.name.replace('.md', '')}
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
};

// Chatbot Component
const DocChatbot = ({ isOpen, onToggle }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Initialize chatbot when opened for the first time
    useEffect(() => {
        if (isOpen && !isInitialized) {
            initializeChatbot();
        }
    }, [isOpen, isInitialized]);

    const initializeChatbot = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
            const response = await fetch(`${API_BASE_URL}/api/chatbot/initialize`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    documentationPath: 'docs', // Adjust as needed
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to initialize chatbot');
            }

            const data = await response.json();
            // Backend returns session_id (snake_case)
            setSessionId(data.session_id);
            setIsInitialized(true);
            setMessages([
                {
                    type: 'bot',
                    content: 'Hello! I\'m your documentation assistant. Ask me anything about the docs!',
                    timestamp: new Date(),
                },
            ]);
        } catch (err) {
            setError('Failed to initialize chatbot. Please try again.');
            console.error('Chatbot initialization error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!inputValue.trim() || isLoading) return;

        const userMessage = {
            type: 'user',
            content: inputValue,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);
        setError(null);

        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
            const response = await fetch(`${API_BASE_URL}/api/chatbot/query`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    session_id: sessionId,
                    query: inputValue,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to get response');
            }

            const data = await response.json();

            const botMessage = {
                type: 'bot',
                content: data.answer || 'I couldn\'t find an answer to that question.',
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, botMessage]);
        } catch (err) {
            setError('Failed to get response. Please try again.');
            console.error('Query error:', err);

            const errorMessage = {
                type: 'bot',
                content: 'Sorry, I encountered an error. Please try again.',
                timestamp: new Date(),
                isError: true,
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`chatbot-panel ${isOpen ? 'open' : ''}`}>
            <div className="chatbot-header">
                <div className="chatbot-header-content">
                    <div className="chatbot-avatar">🤖</div>
                    <div className="chatbot-title">
                        <h4>Doc Assistant</h4>
                        <span className={`chatbot-status ${isInitialized ? 'online' : 'offline'}`}>
                            {isInitialized ? 'Online' : 'Initializing...'}
                        </span>
                    </div>
                </div>
                <button className="chatbot-close" onClick={onToggle}>✕</button>
            </div>

            <div className="chatbot-messages">
                {messages.map((msg, index) => (
                    <div key={index} className={`message ${msg.type} ${msg.isError ? 'error' : ''}`}>
                        <div className="message-content">
                            {msg.type === 'bot' ? (
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        p: ({ node, ...props }) => <p style={{ margin: '0.5em 0' }} {...props} />,
                                        ul: ({ node, ...props }) => <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em' }} {...props} />,
                                        ol: ({ node, ...props }) => <ol style={{ margin: '0.5em 0', paddingLeft: '1.5em' }} {...props} />,
                                        li: ({ node, ...props }) => <li style={{ margin: '0.25em 0' }} {...props} />,
                                        strong: ({ node, ...props }) => <strong style={{ fontWeight: '600' }} {...props} />,
                                        code: ({ node, inline, ...props }) =>
                                            inline ?
                                                <code style={{ background: 'rgba(0,0,0,0.1)', padding: '2px 4px', borderRadius: '3px', fontSize: '0.9em' }} {...props} /> :
                                                <code {...props} />
                                    }}
                                >
                                    {msg.content}
                                </ReactMarkdown>
                            ) : (
                                msg.content
                            )}
                        </div>
                        <div className="message-time">
                            {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="message bot typing">
                        <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {error && (
                <div className="chatbot-error">
                    {error}
                </div>
            )}

            <form className="chatbot-input-form" onSubmit={handleSendMessage}>
                <input
                    type="text"
                    className="chatbot-input"
                    placeholder="Ask about the documentation..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    disabled={!isInitialized || isLoading}
                />
                <button
                    type="submit"
                    className="chatbot-send-btn"
                    disabled={!isInitialized || isLoading || !inputValue.trim()}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                    </svg>
                </button>
            </form>
        </div>
    );
};

const ReportDocumentation = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [content, setContent] = useState('');
    const [error, setError] = useState(null);
    const [chatbotOpen, setChatbotOpen] = useState(false);

    useEffect(() => {
        if (selectedFile) {
            setError(null);

            // Get content from imported documentation
            const docContent = documentationContent[selectedFile.path];
            if (docContent) {
                setContent(docContent);
            } else {
                setError(`Content not found for: ${selectedFile.path}`);
                setContent('');
            }
        }
    }, [selectedFile]);

    // Re-run mermaid when content changes (if there are mermaid blocks in doc)
    useEffect(() => {
        if (content) {
            try {
                mermaid.initialize({ startOnLoad: true, securityLevel: 'loose' });
                mermaid.contentLoaded();
            } catch (e) {
                console.error("Mermaid content load error:", e);
            }
        }
    }, [content]);

    const MarkdownComponents = {
        code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            if (!inline && match && match[1] === 'mermaid') {
                return (
                    <div className="mermaid">
                        {String(children).replace(/\n$/, '')}
                    </div>
                );
            }
            return !inline && match ? (
                <pre className={className}>
                    <code {...props} className={className}>
                        {children}
                    </code>
                </pre>
            ) : (
                <code className={className} {...props}>
                    {children}
                </code>
            );
        }
    };

    return (
        <div className="report-documentation-container">
            <div className="doc-sidebar">
                <h3>Documentation Explorer</h3>
                <div className="sidebar-content">
                    <ul className="sidebar-list">
                        {Object.keys(manifest).map(key => (
                            <SidebarItem
                                key={key}
                                name={key}
                                node={manifest[key]}
                                onSelectFile={(file) => {
                                    setSelectedFile(file);
                                }}
                                selectedFile={selectedFile}
                            />
                        ))}
                    </ul>
                </div>
            </div>

            <div className={`doc-content ${chatbotOpen ? 'chatbot-open' : ''}`}>
                <div className="markdown-view">
                    {error && <div className="error-message">Error: {error}</div>}
                    {selectedFile ? (
                        <>
                            <ReactMarkdown
                                children={content}
                                remarkPlugins={[remarkGfm]}
                                components={MarkdownComponents}
                            />
                        </>
                    ) : (
                        <div className="empty-state">📚 Select a documentation file from the sidebar to view its content</div>
                    )}
                </div>

                {/* Chatbot Toggle Button */}
                <button
                    className="chatbot-toggle-btn"
                    onClick={() => setChatbotOpen(!chatbotOpen)}
                    title="Ask AI about documentation"
                >
                    <span className="toggle-icon">💬</span>
                    <span className="toggle-text">Ask AI</span>
                </button>
            </div>

            {/* Chatbot Panel */}
            <DocChatbot isOpen={chatbotOpen} onToggle={() => setChatbotOpen(!chatbotOpen)} />
        </div>
    );
};

export default ReportDocumentation;
